import {
  keepPreviousData,
  skipToken,
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, rpcClient } from '@/api/rpc/client';
import {
  startRpcDownload,
  startRpcDownloadInFrame,
} from '@/api/rpc/downloadUrl';
import { useToast } from '@/context/ToastContext';
import type {
  As400ExportDivisionCountsResponse,
  As400ExportGroup,
  As400GridSort,
  As400ViewGroup,
  CatalogAdvancedFilter,
  CatalogIdentifierField,
  CatalogMetadataColumn,
  CountItemCatalogInput,
  CountItemCatalogResponse,
  CountItemCatalogScanInput,
  CountItemCatalogScanResponse,
  ExportAs400ViewInput,
  ExportFileFormat,
  ExportItemCatalogInput,
  ListCatalogDivisionsResponse,
  ListCatalogMetadataColumnsResponse,
  ListItemCatalogInput,
  ListItemCatalogResponse,
  ListAs400ExportResponse,
  LocateAs400ExportRowInput,
  LocateAs400ExportRowResponse,
  ResolveItemNumbersResponse,
} from '@erp/rpc';

export type {
  CatalogMetadataColumn,
  ExportAs400ViewInput,
  ExportItemCatalogInput,
  ListItemCatalogResponse,
  ListAs400ExportResponse,
};

export function useItemCatalog(
  input: ListItemCatalogInput,
  options?: { enabled?: boolean },
) {
  return useQuery<ListItemCatalogResponse>({
    ...orpc.inventoryManagement.products.listCatalog.queryOptions({ input }),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Exact count of the catalog rows matching the current filters. Fetched
 * separately from {@link useItemCatalog} because the COUNT(*) can be far
 * slower than a page of rows — the list renders as soon as its page returns
 * and the pager shows "of many" until this resolves. No `keepPreviousData`:
 * a stale count for the previous filters would be wrong, so it reverts to
 * "many" while recounting.
 *
 * Only ProductV3 columns can be counted: a filter on a raw ingest column is
 * resolved by walking the ingest tables, and counting its matches would mean
 * walking all of them. Callers must leave this query disabled while such a
 * filter is active (see `catalogFiltersNeedIngestScan`).
 */
export function useItemCatalogCount(
  input: CountItemCatalogInput,
  options?: { enabled?: boolean },
) {
  return useQuery<CountItemCatalogResponse>({
    ...orpc.inventoryManagement.products.countCatalog.queryOptions({ input }),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The ingest-scan path's counterpart to {@link useItemCatalogCount}: a
 * distinct-product count of a catalog view that a raw-column filter or a
 * Source/Location scope routes through the ingest scan. Same deferred role —
 * fire it only once the page itself has landed — but the answer may be a
 * capped floor rather than a total (`exact` says which), because the server
 * counts by running the same bounded walk the pages come from.
 *
 * No `keepPreviousData`, for the same reason as the plain count: a stale
 * number for the previous filters would be wrong, so the pager reverts to
 * "of many" while recounting. Callers must key the input by the FULL filter
 * set (including `advancedFilterMatch` and the key scope) — a partial input
 * collides with another filter set's cache entry and shows its number.
 */
export function useItemCatalogScanCount(
  input: CountItemCatalogScanInput,
  options?: { enabled?: boolean },
) {
  return useQuery<CountItemCatalogScanResponse>({
    ...orpc.inventoryManagement.products.countCatalogScan.queryOptions({
      input,
    }),
    enabled: options?.enabled ?? true,
  });
}

/**
 * True while a query that produces the item catalog's rows is in flight —
 * either the Gently list or the AS/400 flat view, whichever the page is
 * showing. Read from the query cache rather than threaded out of each view so
 * the page can render one "results are updating" indicator without prop-
 * drilling a fetching flag back up through the AS/400 grid.
 *
 * `keepPreviousData` on the list means a refetch (new term, new filter, new
 * page) leaves the previous rows on screen with no other sign that the result
 * set is changing — this is what the spinner reads.
 *
 * The exact-count query is deliberately excluded: it starts only after the
 * rows land and already has its own "of many" placeholder, so folding it in
 * would leave the spinner running over an already-drawn table.
 */
export function useIsCatalogResultsFetching(): boolean {
  const listFetching = useIsFetching({
    queryKey: orpc.inventoryManagement.products.listCatalog.key(),
  });
  const as400Fetching = useIsFetching({
    queryKey: orpc.inventoryManagement.products.listCatalogAs400View.key(),
  });
  return listFetching + as400Fetching > 0;
}

/**
 * The pasted values, normalized the way the backend matches the chosen
 * identifier — trimmed, and uppercased except for UPCs (digits whose exact
 * spelling is the code) — then deduped and sorted, so the same pasted set
 * always resolves to the same query-cache entry regardless of paste order,
 * casing or duplicates.
 */
export function normalizeItemNumbersForResolution(
  itemNumbers: readonly string[],
  field: CatalogIdentifierField = 'itemNumber',
): string[] {
  const normalized = new Set<string>();
  for (const itemNumber of itemNumbers) {
    const trimmed = itemNumber.trim();
    const folded = field === 'upc' ? trimmed : trimmed.toUpperCase();
    if (folded !== '') normalized.add(folded);
  }
  return Array.from(normalized).sort();
}

/**
 * Resolves a pasted list of identifiers — Rexel item numbers (`displaySku`,
 * the default), supplier part numbers or UPCs — to the org's products: the
 * multiple-items catalog filter's lookup. None is unique, so one input can
 * match several products (one `matched` entry per input × product pair), and
 * inputs matching nothing come back in `unmatchedInputs`.
 */
export function useResolveItemNumbers(
  itemNumbers: readonly string[],
  options?: { enabled?: boolean; field?: CatalogIdentifierField },
) {
  const field = options?.field ?? 'itemNumber';
  const normalized = normalizeItemNumbersForResolution(itemNumbers, field);
  return useQuery<ResolveItemNumbersResponse>({
    ...orpc.inventoryManagement.products.resolveItemNumbers.queryOptions({
      input:
        normalized.length > 0 ? { field, itemNumbers: normalized } : skipToken,
    }),
    enabled: (options?.enabled ?? true) && normalized.length > 0,
  });
}

// Linked schemas change rarely; keep the column suggestions fresh for the
// session rather than refetching per page. Shared with the catalog route
// loader's prefetch so the picker is warm before Advanced Filters opens.
export const CATALOG_METADATA_COLUMNS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Distinct raw metadata columns available as advanced-filter targets, with
 * descriptions — an app-DB-only lookup against the linked schemas' field
 * definitions (no ingest rows are read).
 */
export function useCatalogMetadataColumns(options?: { enabled?: boolean }) {
  return useQuery<ListCatalogMetadataColumnsResponse>({
    ...orpc.inventoryManagement.products.listMetadataColumns.queryOptions({
      input: {},
    }),
    enabled: options?.enabled ?? true,
    staleTime: CATALOG_METADATA_COLUMNS_STALE_TIME_MS,
  });
}

/**
 * The divisions (DataSources) that participate in the item catalog — what the
 * catalog's Source filter and the AS/400 views' division selectors offer.
 * Deliberately narrower than the org's full DataSource list: other feeds keep
 * their own per-division sources (the GAINS ingest repeats the same division
 * titles), which would duplicate every division with a button no product can
 * match. App-DB-only lookup; divisions change only when a data source is
 * (re)configured, so they stay fresh for the session like the metadata
 * columns do.
 */
export function useCatalogDivisions() {
  return useQuery<ListCatalogDivisionsResponse>({
    ...orpc.inventoryManagement.products.listCatalogDivisions.queryOptions({
      input: {},
    }),
    staleTime: CATALOG_METADATA_COLUMNS_STALE_TIME_MS,
  });
}

/**
 * Flattened, paginated view of the ingest metadata records behind one AS/400
 * export file format, scoped to one change set's products and to the selected
 * division(s) (DataSources). Several divisions are combined into one view
 * (their rows concatenated). Rows carry staged-value overlays and the pageInfo
 * carries exact totals. `advancedFilters` are resolved by the ingest server;
 * `locations` (location codes or whole CDB keys) narrow the rows by their own
 * key. Passing a null `group` or an empty `dataSourceIds` disables the query.
 */
export function useAs400ExportView(
  group: As400ExportGroup | null,
  dataSourceIds: string[],
  page: number,
  pageSize: number,
  advancedFilters: CatalogAdvancedFilter[],
  changeSetId: string,
  locations?: string[],
  sort?: As400GridSort | null,
) {
  const hasDivision = dataSourceIds.length > 0;
  return useQuery<ListAs400ExportResponse>({
    ...orpc.inventoryManagement.products.listAs400Export.queryOptions({
      input:
        group && hasDivision
          ? {
              group,
              dataSourceIds,
              page,
              pageSize,
              advancedFilters,
              locations,
              changeSetId,
              sort: sort ?? undefined,
            }
          : skipToken,
    }),
    // No keepPreviousData: switching group/division/page/filters should surface
    // the loading skeleton (the ordered query can be slow), not show stale rows.
    enabled: group !== null && hasDivision,
  });
}

/**
 * Where an ingest row sits in a review worksheet under the grid's filters and
 * sort: the page that shows it and its grid row key, or null when they hide
 * it. Asked once per jump to a comment's cell, so not cached.
 */
export function locateAs400ExportRow(
  input: LocateAs400ExportRowInput,
): Promise<LocateAs400ExportRowResponse> {
  return rpcClient.inventoryManagement.products.locateAs400ExportRow(input);
}

/**
 * Which divisions the review's worksheet actually has records for — what the
 * division selector greys the empty ones out by. Asked for every division the
 * org has, not the selected ones: the answer decides what is selectable.
 *
 * Cheap enough to ask on every worksheet switch (one indexed app-DB read, no
 * ingest rows), and it changes only when the review does, so a staged edit or a
 * new row invalidates it through the change set's own query keys.
 */
export function useAs400ExportDivisionCounts(
  group: As400ExportGroup | null,
  dataSourceIds: string[],
  changeSetId: string,
) {
  const hasDivision = dataSourceIds.length > 0;
  return useQuery<As400ExportDivisionCountsResponse>({
    ...orpc.inventoryManagement.products.as400ExportDivisionCounts.queryOptions(
      {
        input:
          group && hasDivision
            ? { group, dataSourceIds, changeSetId }
            : skipToken,
      },
    ),
    enabled: group !== null && hasDivision,
  });
}

/**
 * Fetches {@link useAs400ExportDivisionCounts}' answer for a worksheet that
 * isn't open (a jump to a cell there), through the same cache, so opening the
 * worksheet afterwards reuses it.
 */
export function useFetchAs400ExportDivisionCounts() {
  const queryClient = useQueryClient();
  return (
    group: As400ExportGroup,
    dataSourceIds: string[],
    changeSetId: string,
  ) =>
    queryClient.fetchQuery<As400ExportDivisionCountsResponse>(
      orpc.inventoryManagement.products.as400ExportDivisionCounts.queryOptions({
        input: { group, dataSourceIds, changeSetId },
      }),
    );
}

/** The catalog Search filter that scopes the AS/400 flat view's rows. */
export interface CatalogFlatViewFilters {
  search?: string;
  searchMode: 'text' | 'regex';
  advancedFilters?: CatalogAdvancedFilter[];
  // Kept in step with the Gently list's SKU sort so both views walk the ingest
  // records in the same order.
  sortDirection?: 'asc' | 'desc';
  // Divisions (DataSources) the rows may come from; omitted = every linked
  // source (each row names its own in the Division column).
  dataSourceIds?: string[];
  // Locations (or whole CDB keys) the rows may be keyed by; omitted = every
  // location.
  locations?: string[];
  // Only these products' records, or only the item list's; omitted = every
  // product matching the search. An empty `keys` shows nothing (no request).
  keys?: string[];
  itemListId?: string;
}

/**
 * The ingest records matching the catalog filter (or belonging to the change
 * set, on the review page), each projected onto one "AS/400 View" column group —
 * only matching records, never a matched product's other rows. In catalog scope
 * the page is cursor-addressed (`cursor`, from the previous page's
 * `pageInfo.nextCursor`); in change-set scope it pages by number. A null `group`
 * disables the query.
 */
export function useCatalogAs400View(
  group: As400ViewGroup | null,
  filters: CatalogFlatViewFilters,
  page: number,
  pageSize: number,
  changeSetId?: string,
  cursor?: string,
) {
  return useQuery<ListAs400ExportResponse>({
    ...orpc.inventoryManagement.products.listCatalogAs400View.queryOptions({
      input:
        group && filters.keys?.length !== 0
          ? {
              group,
              page,
              pageSize,
              search: filters.search,
              searchMode: filters.searchMode,
              sortDirection: filters.sortDirection,
              advancedFilters: filters.advancedFilters,
              dataSourceIds: filters.dataSourceIds,
              locations: filters.locations,
              keys: filters.keys,
              itemListId: filters.itemListId,
              changeSetId,
              cursor,
            }
          : skipToken,
    }),
    // No keepPreviousData: switching group/page/filters should surface the
    // loading skeleton, not stale rows (mirrors useAs400ExportView).
    enabled: group !== null && filters.keys?.length !== 0,
  });
}

/** Supported item-catalog export formats. */
export type ExportItemCatalogFormat = ExportFileFormat;

// The exports resolve to a streamed file rather than a JSON value, so they run
// as a browser navigation to the procedure's RPC GET URL instead of through the
// typed client — see `startRpcDownload`. Taking the path from the query utils
// keeps it in step with the contract: renaming a procedure breaks this line.
const [EXPORT_CATALOG_PATH] =
  orpc.inventoryManagement.products.exportCatalog.key();
const [EXPORT_AS400_VIEW_PATH] =
  orpc.inventoryManagement.products.exportAs400View.key();

/**
 * Triggers the item catalog export in the requested format (CSV or XLSX).
 * Honors an explicit key set (ticked rows / pasted items) or else the whole
 * filtered view; ignores pagination and the on-screen sort — the export pages
 * by key for a cheap, stable cursor — no row cap.
 */
export function useExportItemCatalog() {
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: (input: ExportItemCatalogInput) =>
      // Hidden-frame navigation: `mutate` runs after the click's user
      // activation is gone, so `window.open` is popup-blocked (Safari after
      // any await; Chrome often too). The iframe still streams the attachment.
      startRpcDownloadInFrame(EXPORT_CATALOG_PATH, input),
    onSuccess: (_data, input) => {
      showToast({
        message:
          input.format === 'xlsx'
            ? gt(
                'Your export has started downloading. If the Excel file would be too large to open, it arrives as a zip of smaller workbooks.',
                {
                  context:
                    'toast when an item catalog Excel export begins downloading',
                },
              )
            : gt('Your export has started downloading.', {
                context:
                  'toast when an item catalog CSV export begins downloading',
              }),
        title: gt('Export started', {
          context: 'toast title when an item catalog export download begins',
        }),
        color: 'info',
        icon: 'info',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to export the item catalog',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}

/**
 * Triggers the flattened AS/400 export view download — the rows the review
 * changes or adds, unbounded and streamed.
 *
 * One file per division and per mode: the AS/400 upload runs one mode per file,
 * so a division's authored new rows download as a `…-adds` file separate from
 * the `…-changes` file for its edits to existing records. One division with no
 * new rows is therefore still a single CSV/XLSX; anything more is a `.zip`.
 *
 * A review with nothing changed is reported as a toast instead of downloading a
 * header-only file. That has to be asked before starting the download rather
 * than handled while it runs: the download is a browser navigation, so anything
 * the server returns in place of a file would land in a new tab, not here.
 */
export function useExportAs400View() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: ExportAs400ViewInput) => {
      // The view alone decides whether there is anything to export — the file
      // format, the optional-column rule and the CSV quoting only shape a row
      // already in it.
      const { hasRows } =
        await rpcClient.inventoryManagement.products.as400ExportHasRows({
          group: input.group,
          dataSourceIds: input.dataSourceIds,
          advancedFilters: input.advancedFilters,
          locations: input.locations,
          changeSetId: input.changeSetId,
          modeOverride: input.modeOverride,
        });
      if (!hasRows) return { started: false };
      await startRpcDownload(EXPORT_AS400_VIEW_PATH, input);
      return { started: true };
    },
    onSuccess: ({ started }) => {
      showToast(
        started
          ? {
              message:
                'Your export has started downloading. Large views can take a few minutes.',
              title: 'Export started',
              color: 'info',
              icon: 'info',
            }
          : {
              message:
                'This review has no changes for the selected divisions, so there is nothing to upload.',
              title: 'Nothing to export',
              color: 'info',
              icon: 'info',
            },
      );
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to export the AS/400 view',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
